import React, { useState, useEffect } from 'react';
import { Card, Table, Typography, Button, Modal, Input, DatePicker, Empty, message, Collapse, Tag } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';

const Invoice = () => {
    const [partners, setPartners] = useState([]);
    const [selectedPartner, setSelectedPartner] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentDate, setPaymentDate] = useState(dayjs());
    const [loading, setLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [expandedInvoices, setExpandedInvoices] = useState({});

    // Fetch partners data
    useEffect(() => {
        fetchPartners();
    }, []);

    const fetchPartners = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/admins/partners/invoices');
            setPartners(response.data);
        } catch (error) {
            console.error('Error fetching partners:', error);
            message.error('Failed to fetch partners data');
        } finally {
            setLoading(false);
        }
    };

    const fetchPartnerHistory = async (partnerId) => {
        setHistoryLoading(true);
        try {
            const response = await axios.get(`/admins/partners/${partnerId}/invoice-history`);
            setPaymentHistory(response.data);
        } catch (error) {
            console.error('Error fetching payment history:', error);
            message.error('Failed to fetch payment history');
        } finally {
            setHistoryLoading(false);
        }
    };

    const handlePartnerClick = async (partner) => {
        setSelectedPartner(partner);
        setIsModalVisible(true);
        await fetchPartnerHistory(partner._id);
    };

    const handleCloseModal = () => {
        setIsModalVisible(false);
        setSelectedPartner(null);
        setPaymentAmount('');
        setPaymentDate(dayjs());
        setPaymentHistory([]);
        setExpandedInvoices({});
    };

    const handleAddPayment = async () => {
        if (!selectedPartner || !paymentAmount || !paymentDate) {
            message.warning('Please fill in all payment details');
            return;
        }

        try {
            await axios.post(`/admins/partners/${selectedPartner._id}/payments`, {
                sum: parseFloat(paymentAmount),
                date: paymentDate.toISOString(),
            });

            message.success('Payment added successfully');
            fetchPartners();
            fetchPartnerHistory(selectedPartner._id);
            handleCloseModal();
        } catch (error) {
            console.error('Error adding payment:', error);
            message.error(error.response?.data?.message || 'Failed to add payment');
        }
    };
    // const toggleInvoiceExpansion = (invoiceId) => {
    //     setExpandedInvoices(prev => ({
    //         ...prev,
    //         [invoiceId]: !prev[invoiceId]
    //     }));
    // };

    const columns = [
        {
            title: 'Partner Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Total Outstanding',
            dataIndex: 'outstandingAmount',
            key: 'outstandingAmount',
            render: (amount) => `$${amount.toFixed(2)}`,
        },
        {
            title: 'Last Payment',
            dataIndex: 'lastPaymentDate',
            key: 'lastPaymentDate',
            render: (date) => date ? dayjs(date).format('MM/DD/YYYY') : 'No payments',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={status === 'Paid' ? 'green' : 'orange'}>
                    {status}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Button type="primary" onClick={() => handlePartnerClick(record)}>
                    View Details
                </Button>
            ),
        },
    ];

    const paymentHistoryColumns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (date) => dayjs(date).format('MM/DD/YYYY'),
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount) => `$${amount.toFixed(2)}`,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color="green">{status}</Tag>
            ),
        },
    ];

    const renderInvoiceDetails = (invoices) => {
        if (!invoices || invoices.length === 0) {
            return <Empty description="No invoices found" />;
        }

        return (
            <Collapse>
                {invoices.map((invoice) => (
                    <Collapse.Panel
                        key={invoice._id}
                        header={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>
                                    {dayjs(invoice.month).format('MMMM YYYY')}
                                </span>
                                <span>
                                    Total: ${invoice.totalAmount.toFixed(2)}
                                </span>
                            </div>
                        }
                    >
                        <Table
                            dataSource={invoice.jobs}
                            rowKey="_id"
                            pagination={false}
                            columns={[
                                {
                                    title: 'Job Title',
                                    dataIndex: 'title',
                                    key: 'title',
                                },
                                {
                                    title: 'Date',
                                    dataIndex: 'date',
                                    key: 'date',
                                    render: (date) => dayjs(date).format('MM/DD/YYYY'),
                                },
                                {
                                    title: 'Patient',
                                    dataIndex: 'patient',
                                    key: 'patient',
                                    render: (patient) => `${patient.name} ${patient.surname}`,
                                },
                                {
                                    title: 'Amount',
                                    dataIndex: 'price',
                                    key: 'price',
                                    render: (price) => `$${price.toFixed(2)}`,
                                },
                                {
                                    title: 'Status',
                                    dataIndex: 'status',
                                    key: 'status',
                                    render: (status) => (
                                        <Tag color={
                                            status === 'completed' ? 'green' :
                                                status === 'canceled' ? 'red' :
                                                    status === 'pending' ? 'orange' : 'default'
                                        }>
                                            {status}
                                        </Tag>
                                    ),
                                },
                            ]}
                        />
                    </Collapse.Panel>
                ))}
            </Collapse>
        );
    };

    return (
        <div className="invoice-page">
            <div className="page-header">
                <Typography.Title level={2}>Partner Invoices</Typography.Title>
            </div>

            <Card>
                <Table
                    columns={columns}
                    dataSource={partners}
                    rowKey="_id"
                    loading={loading}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                    }}
                />
            </Card>

            <Modal
                title={`${selectedPartner?.name} - Invoice Details`}
                open={isModalVisible}
                onCancel={handleCloseModal}
                footer={[
                    <Button key="close" onClick={handleCloseModal}>
                        Close
                    </Button>,
                ]}
                width={1000}
            >
                {selectedPartner && (
                    <>
                        <Card title="Add Payment" style={{ marginBottom: 16 }}>
                            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                                <Input
                                    placeholder="Amount"
                                    type="number"
                                    value={paymentAmount}
                                    onChange={(e) => setPaymentAmount(e.target.value)}
                                    style={{ width: '50%' }}
                                />
                                <DatePicker
                                    value={paymentDate}
                                    onChange={(date) => setPaymentDate(date)}
                                    style={{ width: '50%' }}
                                />
                            </div>
                            <Button
                                type="primary"
                                onClick={handleAddPayment}
                                disabled={!paymentAmount || !paymentDate}
                            >
                                Add Payment
                            </Button>
                        </Card>

                        <Card title="Monthly Invoices" style={{ marginBottom: 16 }}>
                            {renderInvoiceDetails(selectedPartner.invoices)}
                        </Card>

                        <Card title="Payment History">
                            <Table
                                columns={paymentHistoryColumns}
                                dataSource={paymentHistory}
                                rowKey="_id"
                                pagination={false}
                                loading={historyLoading}
                            />
                        </Card>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default Invoice;
